'use client';

import { Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { PROCESSOR_TYPE_OPTIONS } from '@/app/processors/create/processor-metadata';
import { SetupConnectFormPanel } from '@/components/setup/experience/SetupConnectFormShell';
import { SetupConnectStepSection } from '@/components/setup/experience/SetupConnectStepSection';
import {
  SetupPaymentAddProcessorDialog,
  type SetupPaymentProcessorConfiguredSelection,
} from '@/components/setup/experience/SetupPaymentAddProcessorDialog';
import { SetupPaymentConnectedProcessorList } from '@/components/setup/experience/SetupPaymentConnectedProcessorList';
import { SetupPaymentLiveCard } from '@/components/setup/experience/SetupPaymentLiveCard';
import {
  SetupPaymentAddCard,
  SetupPaymentOptionCard,
} from '@/components/setup/experience/SetupPaymentProcessorCard';
import { SetupStepFooter } from '@/components/setup/experience/SetupStepFooter';
import {
  SETUP_PATH_MOTION,
  getRecommendedPaymentProcessor,
  type SetupPaymentProcessorType,
} from '@/components/setup/experience/constants';
import { useSetupActivePayment } from '@/components/setup/experience/hooks/useSetupActivePayment';
import { useSetupCommitPayment } from '@/components/setup/experience/hooks/useSetupCommitPayment';
import { buildSetupPaymentSummaryFromType } from '@/components/setup/experience/lib/setupPaymentSummary';
import {
  getSetupBoundPaymentSelection,
  isQuickPaymentType,
  resolveSetupLivePayment,
  type SetupPaymentSelection,
} from '@/components/setup/experience/lib/setupPaymentUtils';
import { useTagadapayApply } from '@/hooks/use-tagadapay-apply';
import { useProcessorsList, useTagadapayAccounts } from '@/lib/api/processors';
import { isTestEnvironment } from '@/lib/payment-constants';
import { parseTRPCError, type ParsedTRPCError } from '@/lib/utils/trpc-error-utils';

/** Recognizable brands previewed on the "use your own provider" card (icons live in /public). */
const OWN_PROVIDER_LOGOS = [
  { src: '/integrations/stripe-icon.svg', alt: 'Stripe' },
  { src: '/integrations/adyen-icon.svg', alt: 'Adyen' },
  { src: '/integrations/nmi-icon.png', alt: 'NMI' },
];

interface SetupPaymentStepProps {
  storeId: string;
  storeName: string;
  baseCurrency: string;
  onBack: () => void;
  /** Processor committed → advance to checkout. */
  onContinue: () => void;
}

export function SetupPaymentStep({
  storeId,
  storeName,
  baseCurrency,
  onBack,
  onContinue,
}: SetupPaymentStepProps) {
  const { t } = useTranslation('setup');
  const xp = 'experience.payment';

  const { active, isLoading: isActiveLoading } = useSetupActivePayment(storeId);
  const {
    data: processors,
    isLoading: isProcessorsLoading,
    isError: isProcessorsError,
  } = useProcessorsList();
  const {
    data: tagadapayAccounts,
    isLoading: isAccountsLoading,
    isError: isAccountsError,
  } = useTagadapayAccounts();
  const application = useTagadapayApply();
  const commitPayment = useSetupCommitPayment();
  const [userSelection, setUserSelection] = React.useState<SetupPaymentSelection | null>(null);
  const [formError, setFormError] = React.useState<ParsedTRPCError | null>(null);
  const [isAddProcessorOpen, setIsAddProcessorOpen] = React.useState(false);
  const [configuredProcessor, setConfiguredProcessor] =
    React.useState<SetupPaymentProcessorConfiguredSelection | null>(null);

  const quickOptions = React.useMemo(
    () => PROCESSOR_TYPE_OPTIONS.filter((option) => isQuickPaymentType(option.value)),
    [],
  );

  const livePayment = resolveSetupLivePayment(
    processors ?? [],
    tagadapayAccounts ?? [],
    active?.processorId ?? null,
  );
  const liveProcessorId = livePayment.kind === 'existing' ? livePayment.processorId : null;
  const dbSelection = getSetupBoundPaymentSelection(
    active,
    livePayment,
    processors ?? [],
    tagadapayAccounts ?? [],
  );
  const recommendedType = getRecommendedPaymentProcessor(
    isTestEnvironment(),
    livePayment.kind !== 'unavailable',
  );

  // Latest user pick wins, else the DB-bound payment, else the recommended default.
  const selection: SetupPaymentSelection = userSelection ??
    dbSelection ?? { kind: 'quick', type: recommendedType };

  // Only a processor just configured in this dialog may precede the account-list refetch.
  const customEntry = React.useMemo(() => {
    const entry = configuredProcessor;
    if (!entry) return null;

    const summary = (active && active.processorId === entry.processorId ? active.summary : null) ??
      buildSetupPaymentSummaryFromType(entry.processorType) ?? {
        processorName: entry.processorType,
        processorLogo: '',
      };
    return { processorId: entry.processorId, processorType: entry.processorType, summary };
  }, [configuredProcessor, active]);

  const isCommitting = commitPayment.isPending;
  const isHydrating = isActiveLoading || isProcessorsLoading || isAccountsLoading || application.isLoading;
  const hasLoadError = isProcessorsError || isAccountsError;
  const isLiveUnavailable =
    selection.kind === 'quick' && selection.type === 'tagadapay-router' && livePayment.kind === 'unavailable';
  const showHydrationLoader = isHydrating;

  const handleContinue = async () => {
    if (isCommitting || isHydrating || hasLoadError || isLiveUnavailable) return;
    setFormError(null);

    try {
      await commitPayment.mutateAsync({ storeId, storeName, baseCurrency, selection, active });
      onContinue();
    } catch (error) {
      setFormError(parseTRPCError(error));
    }
  };

  return (
    <div className="flex flex-col">
      <motion.div
        className="flex w-full flex-col gap-0"
        initial={SETUP_PATH_MOTION.actions.initial}
        animate={SETUP_PATH_MOTION.actions.animate}
        transition={{ ...SETUP_PATH_MOTION.actions.transition, delay: 0.1 }}
      >
        <SetupConnectStepSection
          title={t('experience.shell.stepTitles.payment')}
          description={t('experience.shell.stepDescriptions.payment')}
          footer={
            <SetupStepFooter
              backLabel={t('footer.back')}
              onBack={onBack}
              backDisabled={isCommitting}
              primaryLabel={t('footer.continue')}
              onPrimary={() => void handleContinue()}
              primaryDisabled={isHydrating || hasLoadError || isLiveUnavailable}
              isPrimaryLoading={isCommitting}
            />
          }
        >
          <SetupConnectFormPanel>
            {showHydrationLoader ? (
              <div className="flex items-center justify-center gap-2.5 py-16" role="status">
                <Loader2 className="text-primary size-5 animate-spin" aria-hidden />
                <span className="text-muted-foreground text-sm">{t(`${xp}.loading`)}</span>
              </div>
            ) : (
              <>
                <motion.ul
                  className="m-0 flex list-none flex-col gap-4 p-0"
                  role="radiogroup"
                  aria-label={t('experience.shell.stepTitles.payment')}
                  variants={SETUP_PATH_MOTION.list}
                  initial="hidden"
                  animate="show"
                >
                  {quickOptions.map((option) => {
                    const type = option.value as SetupPaymentProcessorType;
                    const isBound = dbSelection?.kind === 'quick' && dbSelection.type === type;
                    const isSelected = selection.kind === 'quick' && selection.type === type;
                    const activeChip = isBound ? t(`${xp}.activeChip`) : null;
                    const isRecommended = type === recommendedType && !active;

                    if (type === 'tagadapay-router') {
                      return (
                        <SetupPaymentLiveCard
                          key={type}
                          livePayment={livePayment}
                          application={application}
                          logoSrc={option.logo}
                          isSelected={isSelected}
                          activeChip={activeChip}
                          isRecommended={isRecommended}
                          disabled={isCommitting}
                          onSelect={() => setUserSelection({ kind: 'quick', type })}
                        />
                      );
                    }

                    return (
                      <SetupPaymentOptionCard
                        key={option.value}
                        title={t(`${xp}.cards.${type}.title`)}
                        description={t(`${xp}.cards.${type}.description`)}
                        logoSrc={option.logo}
                        logoFallbackText={type === 'sandbox' ? option.label : undefined}
                        isSelected={isSelected}
                        activeChip={activeChip}
                        isRecommended={isRecommended}
                        disabled={isCommitting}
                        onSelect={() => setUserSelection({ kind: 'quick', type })}
                      />
                    );
                  })}

                  <SetupPaymentConnectedProcessorList
                    processors={processors}
                    tagadapayAccounts={tagadapayAccounts ?? []}
                    activeProcessorId={active?.processorId ?? null}
                    liveProcessorId={liveProcessorId}
                    selection={selection}
                    customEntry={customEntry}
                    disabled={isCommitting}
                    onSelect={(processorId, processorType) =>
                      setUserSelection({ kind: 'custom', processorId, processorType })
                    }
                  />

                  <SetupPaymentAddCard
                    title={t(`${xp}.cards.add.title`)}
                    description={t(`${xp}.cards.add.subtitle`)}
                    providerLogos={OWN_PROVIDER_LOGOS}
                    moreLabel={t(`${xp}.cards.add.more`)}
                    disabled={isCommitting}
                    onOpen={() => setIsAddProcessorOpen(true)}
                  />
                </motion.ul>

                {isLiveUnavailable && (
                  <p className="text-muted-foreground m-0 mt-4 text-sm" role="status">
                    {t(`${xp}.liveUnavailable`)}
                  </p>
                )}
                {hasLoadError && (
                  <p className="text-destructive m-0 mt-4 text-sm" role="alert">
                    {t(`${xp}.loadError`)}
                  </p>
                )}

                {formError ? (
                  <p
                    className="text-destructive m-0 mt-4 rounded-md bg-[color-mix(in_srgb,var(--destructive)_8%,var(--background))] px-3 py-2.5 text-xs"
                    role="alert"
                  >
                    {formError.message}
                  </p>
                ) : null}
              </>
            )}
          </SetupConnectFormPanel>
        </SetupConnectStepSection>
      </motion.div>

      <SetupPaymentAddProcessorDialog
        open={isAddProcessorOpen}
        onOpenChange={setIsAddProcessorOpen}
        onProcessorConfigured={({ processorId, processorType }) => {
          setConfiguredProcessor({ processorId, processorType });
          setUserSelection({ kind: 'custom', processorId, processorType });
        }}
      />
    </div>
  );
}
